import React, { useState } from 'react';
import {
  useHourlyTransitionHistory,
  type HourlyTransitionLog,
  type RemainingHourSnapshot,
} from '../hooks/useHourlyTransition';

// ============================================
// HELPERS
// ============================================

const formatHour = (hour: number): string => {
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${hour}:00 AM`;
  if (hour === 12) return '12:00 PM';
  return `${hour - 12}:00 PM`;
};

const formatCurrency = (amount: number): string => {
  return '$' + Math.round(amount).toLocaleString('es-CO');
};

const getTodayStr = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getStatusColor = (pct: number): string => {
  if (pct >= 100) return '#10b981';
  if (pct >= 80) return '#f59e0b';
  return '#ef4444';
};

const getTrendConfig = (trend: string | null): { label: string; color: string; icon: string } => {
  switch (trend) {
    case 'acelerando': return { label: 'Acelerando', color: '#10b981', icon: '▲' };
    case 'desacelerando': return { label: 'Desacelerando', color: '#ef4444', icon: '▼' };
    case 'estable': return { label: 'Estable', color: '#3b82f6', icon: '■' };
    case 'erratico': return { label: 'Errático', color: '#f97316', icon: '◆' };
    case 'evaluando': return { label: 'Evaluando', color: '#8b5cf6', icon: '●' };
    default: return { label: 'Sin datos', color: '#64748b', icon: '○' };
  }
};

const getConfidenceGradient = (level: number): string => {
  if (level >= 70) return 'linear-gradient(90deg, #10b981, #34d399)';
  if (level >= 40) return 'linear-gradient(90deg, #f59e0b, #fbbf24)';
  return 'linear-gradient(90deg, #ef4444, #f87171)';
};

// ============================================
// SUBCOMPONENTES
// ============================================

const TransitionCard: React.FC<{ transition: HourlyTransitionLog }> = ({ transition }) => {
  const [expanded, setExpanded] = useState(false);
  const t = transition;
  const statusColor = getStatusColor(t.posAdaptivePercentage);
  const trendCfg = getTrendConfig(t.trendAssessment);

  return (
    <div className="htp-card">
      {/* Timeline dot + line */}
      <div className="htp-timeline-marker">
        <div className="htp-dot" style={{ borderColor: statusColor, boxShadow: `0 0 8px ${statusColor}40` }} />
        <div className="htp-line" />
      </div>

      {/* Card content */}
      <div className="htp-card-body">
        {/* Header - siempre visible */}
        <button className="htp-card-header" onClick={() => setExpanded(!expanded)}>
          <div className="htp-header-left">
            <span className="htp-hour">{formatHour(t.completedHour)}</span>
            <span className="htp-arrow">{formatHour(t.completedHour + 1)}</span>
          </div>

          <div className="htp-header-center">
            <div className="htp-pct-group">
              <span className="htp-pct-label">POS</span>
              <span className="htp-pct-value" style={{ color: statusColor }}>
                {t.posAdaptivePercentage}%
              </span>
            </div>
            <div className="htp-pct-group">
              <span className="htp-pct-label">IMP</span>
              <span className="htp-pct-value" style={{ color: getStatusColor(t.taxAdaptivePercentage) }}>
                {t.taxAdaptivePercentage}%
              </span>
            </div>
          </div>

          <div className="htp-header-right">
            <span className="htp-trend-badge" style={{ color: trendCfg.color, borderColor: `${trendCfg.color}40` }}>
              {trendCfg.icon} {trendCfg.label}
            </span>
            <span className={`htp-chevron ${expanded ? 'open' : ''}`}>▾</span>
          </div>
        </button>

        {/* Expandable content */}
        {expanded && (
          <div className="htp-card-content">
            {/* Grid comparativo */}
            <div className="htp-section">
              <div className="htp-section-title">Detalle de la Hora Completada</div>
              <div className="htp-compare-grid">
                <div className="htp-compare-col">
                  <div className="htp-compare-header">POS (Modo Especial)</div>
                  <CompareRow label="Meta Original" value={formatCurrency(t.posOriginalTarget)} />
                  <CompareRow label="Meta Adaptativa" value={formatCurrency(t.posAdaptiveTarget)} />
                  <CompareRow label="Venta Real" value={formatCurrency(t.posActualSales)} highlight />
                  <CompareRow label="Ventas" value={`${t.posSalesCount}`} />
                  <CompareRow label="% Original" value={`${t.posOriginalPercentage}%`} />
                  <CompareRow label="% Adaptativo" value={`${t.posAdaptivePercentage}%`} color={statusColor} />
                </div>
                <div className="htp-compare-col">
                  <div className="htp-compare-header">Impuestos (Electrónicas)</div>
                  <CompareRow label="Meta Original" value={formatCurrency(t.taxOriginalTarget)} />
                  <CompareRow label="Meta Adaptativa" value={formatCurrency(t.taxAdaptiveTarget)} />
                  <CompareRow label="Impuesto Real" value={formatCurrency(t.taxActualAmount)} highlight />
                  <CompareRow label="Facturas" value={`${t.taxSalesCount}`} />
                  <CompareRow label="% Original" value={`${t.taxOriginalPercentage}%`} />
                  <CompareRow label="% Adaptativo" value={`${t.taxAdaptivePercentage}%`} color={getStatusColor(t.taxAdaptivePercentage)} />
                </div>
              </div>

              {/* Acumulado del día */}
              <div className="htp-daily-bar">
                <div className="htp-daily-item">
                  <span>Acum. POS:</span>
                  <strong>{formatCurrency(t.dailyPosCumulativeSales)} / {formatCurrency(t.dailyPosTarget)}</strong>
                  <span style={{ color: getStatusColor(t.dailyPosOverallPercentage) }}>
                    ({t.dailyPosOverallPercentage}%)
                  </span>
                </div>
                <div className="htp-daily-item">
                  <span>Acum. IMP:</span>
                  <strong>{formatCurrency(t.dailyTaxCumulativeAmount)} / {formatCurrency(t.dailyTaxTarget)}</strong>
                  <span style={{ color: getStatusColor(t.dailyTaxOverallPercentage) }}>
                    ({t.dailyTaxOverallPercentage}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Redistribución */}
            {t.remainingHoursSnapshot && t.remainingHoursSnapshot.length > 0 && (
              <div className="htp-section">
                <div className="htp-section-title">Redistribución de Próximas Horas</div>
                <div className="htp-redist-table">
                  <div className="htp-redist-header">
                    <span>Hora</span>
                    <span>Meta Adaptativa</span>
                    <span>% del Día</span>
                  </div>
                  {t.remainingHoursSnapshot.map((rh: RemainingHourSnapshot) => (
                    <div key={rh.hour} className="htp-redist-row">
                      <span className="htp-redist-hour">{formatHour(rh.hour)}</span>
                      <span>{formatCurrency(rh.posAdaptiveTarget)}</span>
                      <span className="htp-redist-pct">{rh.posAssignedPercentage}%</span>
                    </div>
                  ))}
                </div>
                {t.recalculationExplanation && (
                  <div className="htp-explanation">
                    {t.recalculationExplanation.split('\n').map((line, i) => (
                      <div key={i} className={line.startsWith('  ') ? 'htp-explanation-indent' : ''}>
                        {line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Opinión del sistema */}
            {t.systemOpinion && (
              <div className="htp-section">
                <div className="htp-section-title">Opinión del Sistema</div>
                <div className="htp-opinion-text">{t.systemOpinion}</div>

                {/* Barra de confianza */}
                <div className="htp-confidence">
                  <div className="htp-confidence-header">
                    <span>Confianza en meta diaria</span>
                    <span className="htp-confidence-value">{t.confidenceLevel}%</span>
                  </div>
                  <div className="htp-confidence-track">
                    <div
                      className="htp-confidence-fill"
                      style={{
                        width: `${Math.min(100, t.confidenceLevel)}%`,
                        background: getConfidenceGradient(t.confidenceLevel),
                      }}
                    />
                  </div>
                  {t.confidenceReason && (
                    <div className="htp-confidence-reason">{t.confidenceReason}</div>
                  )}
                </div>

                {/* MOD state */}
                <div className="htp-mod-state">
                  <span>MOD: {t.modStateAtTransition ? '● ACTIVO' : '○ INACTIVO'}</span>
                  <span>Auto: {t.autoControlEnabled ? 'ON' : 'OFF'}</span>
                  <span>Predictivo: {t.predictiveControlEnabled ? 'ON' : 'OFF'}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CompareRow: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
  color?: string;
}> = ({ label, value, highlight, color }) => (
  <div className={`htp-compare-row ${highlight ? 'highlight' : ''}`}>
    <span className="htp-compare-label">{label}</span>
    <span className="htp-compare-value" style={color ? { color } : undefined}>{value}</span>
  </div>
);

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export const HourlyTransitionPanel: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(getTodayStr());
  const { transitions, loading } = useHourlyTransitionHistory(selectedDate);

  const isToday = selectedDate === getTodayStr();

  return (
    <div className="htp-panel">
      <style>{panelStyles}</style>

      {/* Date picker */}
      <div className="htp-toolbar">
        <div className="htp-date-picker">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="htp-date-input"
          />
          {!isToday && (
            <button className="htp-today-btn" onClick={() => setSelectedDate(getTodayStr())}>
              HOY
            </button>
          )}
        </div>
        <div className="htp-count">
          {transitions.length > 0
            ? `${transitions.length} transicion${transitions.length > 1 ? 'es' : ''} registrada${transitions.length > 1 ? 's' : ''}`
            : ''}
        </div>
      </div>

      {/* Content */}
      {loading && transitions.length === 0 ? (
        <div className="htp-loading">
          <div className="htp-spinner" />
          <span>Cargando historial...</span>
        </div>
      ) : transitions.length === 0 ? (
        <div className="htp-empty">
          <div className="htp-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="htp-empty-title">No hay registros de transición para esta fecha</div>
          <div className="htp-empty-subtitle">
            Las transiciones se registran automáticamente al inicio de cada hora laboral.
          </div>
        </div>
      ) : (
        <div className="htp-timeline">
          {transitions.map((t) => (
            <TransitionCard key={t.id} transition={t} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// ESTILOS (cyberpunk/minimalista)
// ============================================

const panelStyles = `
  .htp-panel {
    padding: 16px;
    min-height: 400px;
  }

  /* Toolbar */
  .htp-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 12px;
    flex-wrap: wrap;
  }

  .htp-date-picker {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .htp-date-input {
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f8fafc;
    color: #1e293b;
    font-size: 0.85rem;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s;
  }

  .htp-date-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }

  .htp-today-btn {
    padding: 8px 14px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: background 0.2s;
  }

  .htp-today-btn:hover {
    background: #2563eb;
  }

  .htp-count {
    font-size: 0.8rem;
    color: #64748b;
  }

  /* Loading */
  .htp-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px 20px;
    color: #64748b;
    font-size: 0.85rem;
  }

  .htp-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: htp-spin 0.8s linear infinite;
  }

  @keyframes htp-spin {
    to { transform: rotate(360deg); }
  }

  /* Empty state */
  .htp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
  }

  .htp-empty-icon {
    color: #94a3b8;
    margin-bottom: 16px;
    opacity: 0.6;
  }

  .htp-empty-title {
    font-size: 1rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
  }

  .htp-empty-subtitle {
    font-size: 0.8rem;
    color: #64748b;
    max-width: 360px;
  }

  /* Timeline */
  .htp-timeline {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .htp-card {
    display: flex;
    gap: 16px;
  }

  /* Timeline marker */
  .htp-timeline-marker {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 20px;
    flex-shrink: 0;
    padding-top: 18px;
  }

  .htp-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #3b82f6;
    background: white;
    flex-shrink: 0;
    z-index: 1;
  }

  .htp-line {
    width: 2px;
    flex: 1;
    background: #e2e8f0;
    margin-top: 4px;
  }

  .htp-card:last-child .htp-line {
    display: none;
  }

  /* Card body */
  .htp-card-body {
    flex: 1;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 12px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .htp-card-body:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  /* Card header */
  .htp-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    color: #1e293b;
    gap: 12px;
  }

  .htp-header-left {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 140px;
  }

  .htp-hour {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  .htp-arrow {
    font-size: 0.75rem;
    color: #94a3b8;
  }

  .htp-arrow::before {
    content: '-> ';
    color: #cbd5e1;
  }

  .htp-header-center {
    display: flex;
    gap: 16px;
  }

  .htp-pct-group {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .htp-pct-label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
  }

  .htp-pct-value {
    font-size: 0.9rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .htp-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .htp-trend-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border: 1px solid;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    background: white;
  }

  .htp-chevron {
    font-size: 1rem;
    color: #94a3b8;
    transition: transform 0.2s;
  }

  .htp-chevron.open {
    transform: rotate(180deg);
  }

  /* Card content (expanded) */
  .htp-card-content {
    border-top: 1px solid #f1f5f9;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: #fafbfc;
    animation: htp-fadeIn 0.2s ease;
  }

  @keyframes htp-fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Sections */
  .htp-section {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 14px;
  }

  .htp-section-title {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #2563eb;
    margin-bottom: 12px;
  }

  /* Compare grid */
  .htp-compare-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .htp-compare-col {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .htp-compare-header {
    font-size: 0.7rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 4px;
  }

  .htp-compare-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 0.8rem;
  }

  .htp-compare-row.highlight {
    background: #eff6ff;
    padding: 4px 6px;
    border-radius: 4px;
    margin: 2px -6px;
  }

  .htp-compare-label {
    color: #64748b;
    font-size: 0.75rem;
  }

  .htp-compare-value {
    color: #1e293b;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* Daily bar */
  .htp-daily-bar {
    display: flex;
    gap: 16px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #e2e8f0;
    flex-wrap: wrap;
  }

  .htp-daily-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.78rem;
    color: #64748b;
  }

  .htp-daily-item strong {
    color: #1e293b;
  }

  /* Redistribution table */
  .htp-redist-table {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 10px;
  }

  .htp-redist-header {
    display: grid;
    grid-template-columns: 100px 1fr 80px;
    gap: 8px;
    padding: 6px 8px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
  }

  .htp-redist-row {
    display: grid;
    grid-template-columns: 100px 1fr 80px;
    gap: 8px;
    padding: 5px 8px;
    font-size: 0.8rem;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
  }

  .htp-redist-row:last-child {
    border-bottom: none;
  }

  .htp-redist-hour {
    color: #64748b;
    font-weight: 500;
  }

  .htp-redist-pct {
    text-align: right;
    color: #2563eb;
    font-weight: 600;
  }

  /* Explanation */
  .htp-explanation {
    background: #f8fafc;
    border-radius: 4px;
    padding: 10px 12px;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.6;
    border-left: 3px solid #e2e8f0;
  }

  .htp-explanation-indent {
    padding-left: 12px;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
  }

  /* Opinion */
  .htp-opinion-text {
    font-size: 0.82rem;
    color: #334155;
    line-height: 1.7;
    margin-bottom: 14px;
  }

  /* Confidence bar */
  .htp-confidence {
    margin-bottom: 10px;
  }

  .htp-confidence-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 0.75rem;
    color: #94a3b8;
  }

  .htp-confidence-value {
    font-weight: 700;
    font-size: 0.9rem;
    color: #0f172a;
  }

  .htp-confidence-track {
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
  }

  .htp-confidence-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.4s ease;
  }

  .htp-confidence-reason {
    margin-top: 6px;
    font-size: 0.7rem;
    color: #64748b;
    line-height: 1.5;
  }

  /* MOD state footer */
  .htp-mod-state {
    display: flex;
    gap: 16px;
    padding-top: 10px;
    border-top: 1px solid #e2e8f0;
    font-size: 0.7rem;
    color: #64748b;
    flex-wrap: wrap;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .htp-compare-grid {
      grid-template-columns: 1fr;
    }

    .htp-header-center {
      display: none;
    }

    .htp-header-left {
      min-width: auto;
    }

    .htp-redist-header,
    .htp-redist-row {
      grid-template-columns: 80px 1fr 60px;
    }
  }
`;
